import { useEffect, useCallback, useState } from "react";
import axios from "axios";
import { Activity, AlertTriangle, Wallet, Users2, Send, Loader2, Truck, RefreshCw, Scale, Trophy, Download } from "lucide-react";
import { toast } from "sonner";
import HelpPopup from "@/components/HelpPopup";
import DrillDrawer from "@/components/DrillDrawer";
import OpsHealthWidget from "@/components/OpsHealthWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Top-stripe colors that mirror the categorized admin nav groups (Admin.jsx
// `NAV_ACCENTS`). Used on every KPI / Mini / Status card so the eye can map
// "emerald stripe → Finance group", "sky stripe → Operations group", etc.
const GROUP_STRIPE = {
    operations: "bg-sky-500",
    catalog: "bg-indigo-500",
    finance: "bg-emerald-500",
    team: "bg-violet-500",
    compliance: "bg-amber-500",
    platform: "bg-rose-500",
};

// Pill styling for the group filter row above the KPIs. Mirrors GROUP_STRIPE
// hues but uses tinted fills so the pills read as "swatch chips" rather than
// stripes. Each entry: { dot, idleRing, activeBg, activeText, activeRing }.
const GROUP_FILTER_PILLS = {
    operations: { label: "Operations", dot: "bg-sky-500",     activeBg: "bg-sky-50",     activeText: "text-sky-800",     activeRing: "ring-sky-300" },
    finance:    { label: "Finance",    dot: "bg-emerald-500", activeBg: "bg-emerald-50", activeText: "text-emerald-800", activeRing: "ring-emerald-300" },
    catalog:    { label: "Catalog",    dot: "bg-indigo-500",  activeBg: "bg-indigo-50",  activeText: "text-indigo-800",  activeRing: "ring-indigo-300" },
    team:       { label: "Team",       dot: "bg-violet-500",  activeBg: "bg-violet-50",  activeText: "text-violet-800",  activeRing: "ring-violet-300" },
    compliance: { label: "Compliance", dot: "bg-amber-500",   activeBg: "bg-amber-50",   activeText: "text-amber-800",   activeRing: "ring-amber-300" },
    platform:   { label: "Platform",   dot: "bg-rose-500",    activeBg: "bg-rose-50",    activeText: "text-rose-800",    activeRing: "ring-rose-300" },
};

function fmtINR(n) {
    // Reports run in QAR per the platform's primary currency. Function name kept for
    // backwards-compat with older imports.
    return "QAR " + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

// Top-3 medal colours for the Employee-of-Month leaderboard. Index 0=gold, 1=silver, 2=bronze; rest fall through.
const RANK_BADGE_CLASS = [
    "bg-amber-400 text-slate-900",
    "bg-slate-200 text-slate-700",
    "bg-amber-100 text-amber-800",
];
const RANK_BADGE_DEFAULT = "bg-slate-100 text-slate-500";

export default function Dashboard() {
    const [d, setD] = useState(null);
    const [eom, setEom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [balancing, setBalancing] = useState(false);
    const [announcing, setAnnouncing] = useState(false);
    const [drill, setDrill] = useState(null); // {metric, title}
    const [wipLimit, setWipLimit] = useState(0);
    const [leads, setLeads] = useState(null);
    // Group filter for the KPI / Mini / Status board. `null` = show all.
    // Only the groups currently rendered on this page are clickable.
    const [groupFilter, setGroupFilter] = useState(null);
    // Visible groups inferred statically — every Kpi/Mini below carries one
    // of these. If we add a Team / Compliance card later, append here.
    const visibleGroups = ["operations", "finance"];
    const passesFilter = (g) => !groupFilter || groupFilter === g;

    const load = useCallback(async () => {
        try {
            const [{ data: dash }, { data: eomData }] = await Promise.all([
                axios.get(`${API}/admin/dashboard`),
                axios.get(`${API}/admin/employee-of-month`),
            ]);
            setD(dash);
            setEom(eomData);
        } catch (e) {
            toast.error("Failed to load dashboard");
        } finally {
            setLoading(false);
        }
        // WIP limit — non-critical, silent fail.
        axios.get(`${API}/admin/settings`)
            .then(({ data }) => setWipLimit(Number(data?.kanban_wip_limit) || 0))
            .catch(() => setWipLimit(0));
        // Product leads (clinic vs lab) — non-critical, silent fail.
        axios.get(`${API}/admin/leads/summary`)
            .then(({ data }) => setLeads(data))
            .catch(() => {});
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const sendSnapshot = async () => {
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/admin/snapshot/send`, {});
            toast.success(`Snapshot sent · ${data.emails_sent}/${data.emails_attempted} emails · ${data.whatsapp_sent}/${data.whatsapp_attempted} WhatsApp`);
        } catch (e) {
            toast.error("Snapshot failed — check Settings");
        } finally {
            setSending(false);
        }
    };

    const rebalance = async () => {
        if (!window.confirm("Auto-balance in-flight cases (today + late + orphaned) across active technicians?")) return;
        setBalancing(true);
        try {
            const { data } = await axios.post(`${API}/admin/cases/rebalance`, {});
            const buckets = data.considered_by_reason || {};
            const breakdown = [
                buckets.today ? `${buckets.today} today` : null,
                buckets.late ? `${buckets.late} late` : null,
                buckets.orphan ? `${buckets.orphan} orphaned` : null,
            ].filter(Boolean).join(" · ");
            if (data.reassigned === 0) {
                toast.success(`Already balanced · ${data.unchanged} case${data.unchanged !== 1 ? "s" : ""} left as-is${breakdown ? ` (${breakdown})` : ""}`);
            } else {
                toast.success(`${data.reassigned} case${data.reassigned !== 1 ? "s" : ""} reassigned · ${data.unchanged} unchanged${breakdown ? ` · pool: ${breakdown}` : ""}`, { duration: 8000 });
            }
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Auto-balance failed");
        } finally {
            setBalancing(false);
        }
    };

    if (loading || !d) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-500" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-dashboard">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="text-[13px] text-slate-500 font-mono-tr tracking-wide">
                    Updated {new Date(d.generated_at).toLocaleString()}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="tr-btn-ghost !py-2 !px-3 text-[12.5px]" data-testid="dashboard-refresh">
                        <RefreshCw size={13} /> Refresh
                    </button>
                    <button onClick={sendSnapshot} disabled={sending} className="tr-btn !py-2.5 !px-4 text-[13px]" data-testid="dashboard-send-snapshot">
                        {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                        Send daily snapshot
                    </button>
                    <HelpPopup
                        testid="help-snapshot"
                        title="Daily snapshot"
                        align="right"
                        items={[
                            { label: "What it sends", body: "An HTML email + WhatsApp message with today's intake, deliveries, revenue, late cases and technician workload." },
                            { label: "Recipients", body: "Configure comma-separated emails in Settings → 'Daily recipients'. WhatsApp uses the numbers in 'WhatsApp recipients' (E.164 format like +97455123456)." },
                            { label: "WhatsApp via", body: "Twilio WhatsApp Business API — paste your Account SID / Auth Token / From number in Settings → Twilio WhatsApp." },
                        ]}
                    />
                </div>
            </div>

            {/* Operational Health — morning-coffee feed */}
            <OpsHealthWidget />

            {/* Group filter pills — color-coded chips that filter the KPI / Mini / Status grids
                by parent nav group. "All" shows everything. Each pill matches the nav accent
                so users build a single mental model of colors across the admin surface. */}
            <div className="flex flex-wrap items-center gap-2" data-testid="dashboard-group-filter">
                <span className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500 mr-1">· Show</span>
                <button
                    type="button"
                    data-testid="dashboard-filter-all"
                    onClick={() => setGroupFilter(null)}
                    aria-pressed={groupFilter === null}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ring-1 ${
                        groupFilter === null
                            ? "bg-slate-900 text-white ring-slate-900"
                            : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-400 hover:text-slate-900"
                    }`}
                >
                    All
                </button>
                {visibleGroups.map((g) => {
                    const pill = GROUP_FILTER_PILLS[g];
                    const active = groupFilter === g;
                    return (
                        <button
                            type="button"
                            key={g}
                            data-testid={`dashboard-filter-${g}`}
                            onClick={() => setGroupFilter(active ? null : g)}
                            aria-pressed={active}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium transition ring-1 ${
                                active
                                    ? `${pill.activeBg} ${pill.activeText} ${pill.activeRing}`
                                    : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-400 hover:text-slate-900"
                            }`}
                        >
                            <span className={`h-1.5 w-1.5 rounded-full ${pill.dot}`} aria-hidden />
                            {pill.label}
                        </button>
                    );
                })}
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {passesFilter("operations") && (
                    <Kpi
                        label="Cases today"
                        value={d.today.cases_received}
                        icon={Activity}
                        accent="text-cyan-700 bg-cyan-50"
                        group="operations"
                        onClick={() => setDrill({ metric: "cases_today", title: "Cases received today" })}
                        testid="kpi-cases-today"
                        help={<HelpPopup testid="help-cases-today" title="Cases today" body="New cases received between 00:00 and now (UTC). Click to see the list." />}
                    />
                )}
                {passesFilter("operations") && (
                    <Kpi
                        label="Delivered today"
                        value={d.today.cases_delivered}
                        icon={Truck}
                        accent="text-emerald-700 bg-emerald-50"
                        group="operations"
                        onClick={() => setDrill({ metric: "delivered_today", title: "Delivered today" })}
                        testid="kpi-delivered-today"
                        help={<HelpPopup testid="help-delivered-today" title="Delivered today" body="Cases moved to status 'Delivered' since 00:00 today. Click for the list." />}
                    />
                )}
                {passesFilter("finance") && (
                    <Kpi
                        label="Revenue today"
                        value={fmtINR(d.today.revenue)}
                        icon={Wallet}
                        accent="text-amber-700 bg-amber-50"
                        group="finance"
                        onClick={() => setDrill({ metric: "revenue_today", title: "Revenue today", subtitle: "Paid invoices" })}
                        testid="kpi-revenue-today"
                        help={<HelpPopup testid="help-rev-today" title="Revenue today" body="Estimated revenue = sum of invoice totals for cases delivered today. Becomes 'realized' once invoice is marked paid." />}
                    />
                )}
                {passesFilter("operations") && (
                    <Kpi
                        label="Late cases"
                        value={d.late.count}
                        icon={AlertTriangle}
                        accent={d.late.count > 0 ? "text-rose-700 bg-rose-50" : "text-slate-700 bg-slate-100"}
                        group="operations"
                        onClick={() => setDrill({ metric: "late_cases", title: "Late cases", subtitle: "Accepted >24h ago, not delivered" })}
                        testid="kpi-late-cases"
                        help={<HelpPopup testid="help-late" title="Late cases" body="Cases that were accepted more than 24 hours ago and still haven't been delivered. Click to see them all." />}
                    />
                )}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
                {passesFilter("operations") && <Mini label="MTD intake" value={d.month.cases_received} group="operations" onClick={() => setDrill({ metric: "mtd_intake", title: "Month-to-date intake" })} testid="mini-mtd-intake" />}
                {passesFilter("operations") && <Mini label="MTD delivered" value={d.month.cases_delivered} group="operations" onClick={() => setDrill({ metric: "mtd_delivered", title: "Month-to-date delivered" })} testid="mini-mtd-delivered" />}
                {passesFilter("finance") && <Mini label="MTD revenue" value={fmtINR(d.month.revenue)} accent group="finance" onClick={() => setDrill({ metric: "mtd_revenue", title: "MTD revenue", subtitle: "Paid invoices this month" })} testid="mini-mtd-revenue" />}
                {passesFilter("operations") && <Mini label="YTD intake" value={d.year.cases_received} group="operations" onClick={() => setDrill({ metric: "ytd_intake", title: "Year-to-date intake" })} testid="mini-ytd-intake" />}
                {passesFilter("operations") && <Mini label="YTD delivered" value={d.year.cases_delivered} group="operations" onClick={() => setDrill({ metric: "ytd_delivered", title: "Year-to-date delivered" })} testid="mini-ytd-delivered" />}
                {passesFilter("finance") && <Mini label="Last month revenue" value={fmtINR(d.last_month.revenue)} accent group="finance" onClick={() => setDrill({ metric: "last_month_revenue", title: "Last month revenue", subtitle: "Paid invoices" })} testid="mini-last-month-revenue" />}
            </div>

            {/* Product leads — which Toothmatrix product is pulling (lab demos vs practice sign-ups) */}
            {leads && passesFilter("operations") && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="product-leads-card">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="tr-overline">· Product leads — clinics vs labs</div>
                        <HelpPopup testid="help-product-leads" title="Product leads" body="Lab leads = demo requests from the Toothmatrix Lab pages (contact subject). Practice leads = clinic registrations on the Scan Inbox portal plus practice enquiries. 30d = last 30 days." />
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-7 gap-3">
                        <Mini label="Lab demo requests" value={leads.lab.demo_requests} group="operations" testid="leads-lab-total" />
                        <Mini label="Demo bookings" value={leads.lab.bookings ?? 0} group="operations" testid="leads-lab-bookings" />
                        <Mini label="Lab leads · 30d" value={leads.lab.last30} group="operations" testid="leads-lab-30d" />
                        <Mini label="Practice registrations" value={leads.practice.registrations} group="operations" testid="leads-practice-regs" />
                        <Mini label="Practices approved" value={leads.practice.approved} group="operations" testid="leads-practice-approved" />
                        <Mini label="Practice enquiries" value={leads.practice.enquiries} group="operations" testid="leads-practice-enquiries" />
                        <Mini label="Practice leads · 30d" value={leads.practice.last30} group="operations" testid="leads-practice-30d" />
                    </div>
                </div>
            )}

            {/* Status breakdown — Operations group; hide entirely when filtering to Finance only */}
            {passesFilter("operations") && (
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                    <div className="tr-overline">· Live status board</div>
                    <HelpPopup testid="help-status" title="Status board" body="A snapshot of every case in the lab right now grouped by status. Click any number to filter the Cases tab to that status." />
                </div>
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
                    {["received","accepted","in_design","manufacturing","out_for_delivery","delivered"].map((s) => (
                        <button
                            key={s}
                            type="button"
                            onClick={() => setDrill({ metric: `status:${s}`, title: `Status · ${s.replace(/_/g, " ")}` })}
                            data-testid={`status-${s}`}
                            data-group="operations"
                            className="relative overflow-hidden text-left rounded-xl border border-slate-200 p-4 hover:border-cyan-400 hover:shadow-sm transition"
                        >
                            <span className={`absolute inset-x-0 top-0 h-[2px] ${GROUP_STRIPE.operations}`} aria-hidden />
                            <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">{s.replace("_"," ")}</div>
                            <div className="font-display text-3xl text-slate-900 tracking-tight mt-1">{d.status_breakdown[s] || 0}</div>
                        </button>
                    ))}
                </div>
            </div>
            )}

            {/* Late preview + Technician workload */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                            <AlertTriangle size={16} className="text-rose-500 shrink-0" />
                            <div className="font-display text-lg text-slate-900 tracking-tight">Late cases</div>
                            <HelpPopup testid="help-late-list" body="Top 10 late cases — accepted more than 24h ago and still not delivered. The technician name shows who is responsible." />
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={rebalance}
                                disabled={balancing}
                                data-testid="auto-balance-btn"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-cyan-200 bg-cyan-50 text-cyan-800 hover:bg-cyan-100 transition-colors text-[12px] font-medium disabled:opacity-50"
                                title="Redistribute today's in-flight cases across active technicians using load-balanced 3-tier routing"
                            >
                                {balancing ? <Loader2 size={13} className="animate-spin" /> : <Scale size={13} />}
                                Auto-balance
                            </button>
                            <HelpPopup
                                testid="help-balance"
                                title="Auto-balance day"
                                items={[
                                    { label: "What it does", body: "Re-runs the smart-routing logic on every accepted-but-undelivered case from today, recomputing workload after each move so the result is genuinely balanced (not round-robin)." },
                                    { label: "Tier order", body: "Preferred technicians on the product → specialty match → any active technician. The least-loaded eligible technician wins each case." },
                                    { label: "Safe to spam", body: "If the lab is already balanced, nothing moves. Each reassignment is recorded in the case status history with a note." },
                                ]}
                            />
                            <span className="text-[12px] font-mono-tr text-slate-500">{d.late.count} late</span>
                        </div>
                    </div>
                    {d.late.preview.length === 0 ? (
                        <EmptyHint testid="empty-late" message="Nothing late — your lab is on time. ✓" />
                    ) : (
                        <ul className="space-y-2">
                            {d.late.preview.map((c) => (
                                <li key={c.case_number} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg border border-slate-100 bg-rose-50/30">
                                    <div className="min-w-0">
                                        <div className="font-mono-tr text-[12.5px] text-slate-900">{c.case_number}</div>
                                        <div className="text-[12px] text-slate-600 truncate">{c.clinic_name} · {c.product.replace("-"," ")}</div>
                                    </div>
                                    <div className="text-[11.5px] font-mono-tr text-rose-700 whitespace-nowrap">
                                        {c.assigned_to_name || "Unassigned"}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="bg-white rounded-2xl border border-slate-200 p-6">
                    <div className="flex items-center gap-2 mb-4">
                        <Users2 size={16} className="text-slate-700" />
                        <div className="font-display text-lg text-slate-900 tracking-tight">Technician workload</div>
                        <HelpPopup
                            testid="help-techs"
                            title="Technician workload"
                            items={[
                                { label: "Today", body: "Cases newly assigned to this technician since midnight." },
                                { label: "In-flight", body: "Cases assigned and not yet delivered — the actual current workload." },
                                { label: "MTD", body: "Cases assigned this calendar month." },
                                { label: "Add technicians", body: "Use the 'Technicians' tab. New cases auto-assign to the technician with the lightest in-flight load whose specialty matches the product." },
                            ]}
                        />
                    </div>
                    {d.technicians.length === 0 ? (
                        <EmptyHint testid="empty-techs" message="No technicians yet. Add them in the Technicians tab — cases auto-assign by specialty." />
                    ) : (
                        <table className="w-full">
                            <thead>
                                <tr className="text-[10px] font-mono-tr tracking-[0.18em] uppercase text-slate-500 text-left">
                                    <th className="pb-2">Name</th>
                                    <th className="pb-2">Specialties</th>
                                    <th className="pb-2 text-right">Today</th>
                                    <th className="pb-2 text-right">In-flight</th>
                                    <th className="pb-2 text-right">MTD</th>
                                </tr>
                            </thead>
                            <tbody>
                                {d.technicians.map((t) => {
                                    const overload = !!wipLimit && t.in_flight >= wipLimit;
                                    return (
                                    <tr key={t.id} className="border-t border-slate-100 text-[13.5px]">
                                        <td className="py-2.5">{t.name}</td>
                                        <td className="py-2.5 text-slate-600 text-[12px]">
                                            <div className="flex flex-wrap gap-1">
                                                {(t.specialties || []).slice(0, 3).map((s) => (
                                                    <span key={s} className="px-1.5 py-0.5 rounded-full text-[10px] font-mono-tr tracking-wider uppercase bg-slate-100 text-slate-600">{s}</span>
                                                ))}
                                                {(t.specialties || []).length > 3 && <span className="text-slate-400">+{t.specialties.length - 3}</span>}
                                                {!(t.specialties || []).length && "—"}
                                            </div>
                                        </td>
                                        <td className="py-2.5 text-right font-mono-tr">{t.today}</td>
                                        <td className="py-2.5 text-right font-mono-tr">
                                            <span
                                                data-testid={`tech-inflight-${t.id}`}
                                                title={overload ? `Over WIP limit (${wipLimit})` : ""}
                                                className={overload ? "inline-block px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 ring-1 ring-amber-300" : ""}
                                            >
                                                {t.in_flight}{overload ? ` / ${wipLimit}` : ""}
                                            </span>
                                        </td>
                                        <td className="py-2.5 text-right font-mono-tr">{t.month}</td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Employee of the Month */}
            <EmployeeOfMonthCard eom={eom} announcing={announcing} onAnnounce={async () => {
                if (!window.confirm("Send the Employee of the Month announcement now via email and WhatsApp?")) return;
                setAnnouncing(true);
                try {
                    const { data } = await axios.post(`${API}/admin/employee-of-month/announce`, {});
                    if (data.announced) {
                        toast.success(`Announced · ${data.emails_sent} emails · ${data.whatsapp_sent} WhatsApp`);
                    } else {
                        toast.message(`Skipped — ${data.reason || "no winner"}`);
                    }
                } catch (e) {
                    toast.error(e?.response?.data?.detail || "Announce failed");
                } finally {
                    setAnnouncing(false);
                }
            }} />

            <DrillDrawer
                open={!!drill}
                onClose={() => setDrill(null)}
                title={drill?.title || ""}
                subtitle={drill?.subtitle}
                kind={drill && /revenue/i.test(drill.metric) ? "invoices" : "cases"}
                fetchUrl={drill ? `/admin/dashboard/drill?metric=${encodeURIComponent(drill.metric)}` : null}
            />
        </div>
    );
}

function EmployeeOfMonthCard({ eom, announcing, onAnnounce }) {
    if (!eom) return null;
    const winner = eom.winner;
    const leaders = (eom.leaderboard || []).filter((r) => r.delivered_cases > 0).slice(0, 5);
    const certHref = winner ? `${API}/admin/employee-of-month/certificate?month=${eom.year}-${String(eom.month_number).padStart(2, "0")}` : null;
    return (
        <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm" data-testid="employee-of-month-card">
            <div className="bg-gradient-to-br from-[#0B1120] via-[#0f1c33] to-[#1e3a5f] text-white p-6 lg:p-8">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                        <div className="h-12 w-12 rounded-xl bg-amber-400/15 text-amber-300 flex items-center justify-center">
                            <Trophy size={22} />
                        </div>
                        <div>
                            <div className="font-mono-tr text-[10.5px] tracking-[0.24em] uppercase text-amber-300">· Employee of the Month</div>
                            <div className="font-display text-3xl tracking-tight mt-1">{winner ? winner.name : "No winner yet"}</div>
                            <div className="text-[13px] text-white/60 mt-0.5">{eom.month} · live ranking</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {winner && (
                            <a href={certHref} target="_blank" rel="noreferrer" data-testid="eom-cert-link" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 text-white text-[12.5px] border border-white/15 transition-colors">
                                <Download size={13} /> Certificate
                            </a>
                        )}
                        <button onClick={onAnnounce} disabled={announcing || !winner} data-testid="eom-announce-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-900 text-[12.5px] font-medium disabled:opacity-50 transition-colors">
                            {announcing ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            Announce now
                        </button>
                        <HelpPopup
                            testid="help-eom"
                            title="Employee of the Month"
                            align="right"
                            items={[
                                { label: "Weighted score", body: "50% revenue + 35% delivered output + 15% on-time delivery rate (vs product SLA). Each metric normalized to 0–100 against the top performer." },
                                { label: "Auto-announce", body: "Runs on the 1st of every month at 09:00 IST. Emails 'daily_recipients' + WhatsApps 'whatsapp_recipients' with the winner + certificate PDF link. Toggle in Settings → Operations." },
                                { label: "Certificate", body: "A4 landscape PDF with the winner's name, monthly stats, weighted score, and a signed footer. Click 'Certificate' to download." },
                            ]}
                        />
                    </div>
                </div>
                {winner && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
                        <EomStat label="Score" value={`${winner.score.toFixed(1)}`} unit="/100" accent />
                        <EomStat label="Delivered" value={winner.delivered_cases} />
                        <EomStat label="Revenue" value={`QAR ${Number(winner.revenue).toLocaleString("en-US", { maximumFractionDigits: 0 })}`} />
                        <EomStat label="On-time" value={`${Math.round(winner.on_time_rate * 100)}%`} />
                    </div>
                )}
            </div>
            {leaders.length > 0 && (
                <div className="bg-white p-5 lg:p-6">
                    <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-slate-500 mb-3">· Top 5 leaderboard</div>
                    {/* Column headers — mirror the same widths as the row layout below */}
                    <div className="hidden sm:flex items-center gap-3 px-3 pb-2 border-b border-slate-100 mb-1.5">
                        <div className="w-7" aria-hidden="true" />
                        <div className="flex-1 min-w-0 font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400">Technician</div>
                        <div className="text-right font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400">Cases</div>
                        <div className="text-right font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400 w-32">Revenue</div>
                        <div className="hidden md:block text-right font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400 w-12">On-time</div>
                        <div className="text-right font-mono-tr text-[10px] tracking-[0.22em] uppercase text-slate-400 w-12">Score</div>
                    </div>
                    <div className="space-y-1.5">
                        {leaders.map((r, i) => (
                            <div key={r.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg ${i === 0 ? "bg-amber-50/60" : "hover:bg-slate-50"}`} data-testid={`eom-rank-${i + 1}`}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-mono-tr font-semibold ${RANK_BADGE_CLASS[i] || RANK_BADGE_DEFAULT}`}>{i + 1}</div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[14px] font-medium text-slate-900 truncate">{r.name}</div>
                                    <div className="text-[11.5px] font-mono-tr text-slate-500 truncate">{(r.specialties || []).join(" · ") || "—"}</div>
                                </div>
                                <div className="hidden sm:block text-right text-[12px] text-slate-600 font-mono-tr">{r.delivered_cases}</div>
                                <div className="hidden sm:block text-right text-[12px] text-slate-600 font-mono-tr w-32">QAR {Number(r.revenue).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                                <div className="hidden md:block text-right text-[12px] font-mono-tr text-slate-600 w-12">{Math.round(r.on_time_rate * 100)}%</div>
                                <div className="text-right text-[13px] font-mono-tr text-cyan-700 font-semibold w-12">{r.score.toFixed(1)}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 text-[11.5px] text-slate-400">Score = 50% revenue + 35% output + 15% on-time</div>
                </div>
            )}
        </div>
    );
}

function EomStat({ label, value, unit, accent }) {
    return (
        <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3">
            <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-white/55">{label}</div>
            <div className={`mt-1 font-display text-2xl tracking-tight ${accent ? "text-amber-300" : "text-white"}`}>
                {value}<span className="text-[14px] text-white/40 font-sans ml-0.5">{unit || ""}</span>
            </div>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, accent, help, onClick, testid, group }) {
    const clickable = !!onClick;
    const stripeClass = group ? GROUP_STRIPE[group] : null;
    return (
        <div
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            data-testid={testid}
            data-group={group || undefined}
            className={`relative bg-white rounded-2xl border border-slate-200 p-5 transition select-none overflow-hidden ${clickable ? "hover:border-cyan-400 hover:shadow-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-300" : ""}`}
        >
            {stripeClass && <span className={`absolute inset-x-0 top-0 h-[3px] ${stripeClass}`} aria-hidden />}
            <div className="flex items-center justify-between mb-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}><Icon size={16} /></div>
                {help}
            </div>
            <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">{label}</div>
            <div className="font-display text-3xl lg:text-4xl text-slate-900 tracking-tight mt-1">{value}</div>
        </div>
    );
}

function Mini({ label, value, accent, onClick, testid, group }) {
    const clickable = !!onClick;
    const stripeClass = group ? GROUP_STRIPE[group] : null;
    return (
        <div
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={clickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
            data-testid={testid}
            data-group={group || undefined}
            className={`relative bg-white rounded-xl border border-slate-200 p-3.5 transition select-none overflow-hidden ${clickable ? "hover:border-cyan-400 hover:shadow-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-cyan-300" : ""}`}
        >
            {stripeClass && <span className={`absolute inset-x-0 top-0 h-[2px] ${stripeClass}`} aria-hidden />}
            <div className="text-[10px] font-mono-tr tracking-[0.18em] uppercase text-slate-500">{label}</div>
            <div className={`font-display text-xl tracking-tight mt-0.5 ${accent ? "text-cyan-700" : "text-slate-900"}`}>{value}</div>
        </div>
    );
}

export function EmptyHint({ message, testid }) {
    return (
        <div data-testid={testid} className="rounded-xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center">
            <div className="text-[13.5px] text-slate-600">{message}</div>
        </div>
    );
}
